import {
  Component,
  Host,
  Prop,
  Element,
  Method,
  getAssetPath,
  h
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { ComboBoxModel } from "@genexus/chameleon-controls-library";

import { Locale } from "../../common/locale";
import { config } from "../../common/config";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/combo-box",
  "components/edit",
  "components/icon",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const DATABASE_ICON = getIconPath({
  category: "system",
  name: "database",
  colorType: "primary"
});

/**
 * Name of the property that toggles integrated security. Its presence in
 * `properties` is what drives the rendering of the Authentication section.
 */
const TRUSTED_CONNECTION = "TRUSTED_CONNECTION";
const USER_ID = "USER_ID";
const USER_PASSWORD = "USER_PASSWORD";

/** Category that groups the authentication-related fields. */
const AUTH_CATEGORY = "authentication";

@Component({
  tag: "gx-ide-db-properties-configurator",
  styleUrl: "db-properties-configurator.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/db-properties-configurator"]
})
export class GxIdeDbPropertiesConfigurator {
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  #chShortcutsEl: HTMLChShortcutsElement;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/db-properties-configurator/shortcuts.json`
  );

  @Element() el: HTMLGxIdeDbPropertiesConfiguratorElement;

  /**
   * The list of properties to configure. Acts as the single source of truth:
   * the dialog reads each value from `DSPropertyField.value` and notifies the
   * host of every edit through `propertyChangeCallback`.
   */
  @Prop() readonly properties: DSPropertyField[] = [];

  /**
   * Callback invoked when the user cancels the configuration.
   */
  @Prop() readonly cancelCallback!: CancelCallback;

  /**
   * Callback invoked when the user confirms the configuration.
   */
  @Prop() readonly confirmCallback!: ConfirmCallback;

  /**
   * Callback invoked on every input change. Receives the name of the
   * property that changed and its new value. The host is expected to apply
   * the change and push an updated `properties` array back to the component.
   */
  @Prop() readonly propertyChangeCallback!: PropertyChangeCallback;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  /**
   * Suspends or reactivates the shortcuts.
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    this.#chShortcutsEl.suspend = suspendShortcuts;
  }

  #getValue = (field: DSPropertyField): string => field.value ?? "";

  #hasPendingRequired = (): boolean =>
    (this.properties ?? []).some(
      field => field.missing && this.#getValue(field).trim() === ""
    );

  #buildComboModel = (field: DSPropertyField): ComboBoxModel =>
    (field.standardValues ?? []).map(value => ({ value, caption: value }));

  #isAuthField = (field: DSPropertyField): boolean =>
    field.name === TRUSTED_CONNECTION ||
    field.name === USER_ID ||
    field.name === USER_PASSWORD ||
    field.category?.toLowerCase() === AUTH_CATEGORY;

  /** Whether the host sent the TRUSTED_CONNECTION property. Only then is the
   * Authentication section rendered. */
  #hasTrustedConnection = (): boolean =>
    (this.properties ?? []).some(field => field.name === TRUSTED_CONNECTION);

  #valueChangeHandler =
    (name: string) => (event: CustomEvent<string> | InputEvent) => {
      const detail = (event as CustomEvent<string>).detail;
      const value =
        typeof detail === "string"
          ? detail
          : ((event.target as HTMLChEditElement)?.value ?? "");
      this.propertyChangeCallback(name, value);
    };

  #cancelButtonClickedHandler = () => {
    this.cancelCallback?.();
  };

  #confirmButtonClickedHandler = () => {
    if (this.#hasPendingRequired()) {
      return;
    }
    this.confirmCallback?.();
  };

  // #region Renders

  #renderControl = (field: DSPropertyField) => {
    const fieldId = `field-${field.name}`;
    const value = this.#getValue(field);

    switch (field.control) {
      case "password":
        return (
          <ch-edit
            id={fieldId}
            class="input"
            type="password"
            showPasswordButton
            debounce={config.inputDebounce}
            value={value}
            onInput={this.#valueChangeHandler(field.name)}
          ></ch-edit>
        );

      case "combo":
        return (
          <ch-combo-box-render
            id={fieldId}
            class="combo-box"
            value={value}
            model={this.#buildComboModel(field)}
            onInput={this.#valueChangeHandler(field.name)}
            onChange={this.#valueChangeHandler(field.name)}
          ></ch-combo-box-render>
        );

      case "checkbox":
        return (
          <ch-checkbox
            id={fieldId}
            class="checkbox"
            value={value}
            checkedValue="Yes"
            unCheckedValue="No"
            onInput={this.#valueChangeHandler(field.name)}
          ></ch-checkbox>
        );

      case "combo-editable":
        return (
          <ch-combo-box-render
            id={fieldId}
            class="combo-box"
            suggest
            suggestDebounce={config.inputDebounce}
            value={value}
            model={this.#buildComboModel(field)}
            onInput={this.#valueChangeHandler(field.name)}
            onChange={this.#valueChangeHandler(field.name)}
            part={`combo-box combo-box--editable combo-box--${field.name}`}
          ></ch-combo-box-render>
        );

      case "custom":
      case "readonly":
        return (
          <ch-edit
            id={fieldId}
            class="input"
            readonly
            value={value}
            part={`input input--${field.control} input--${field.name}`}
          ></ch-edit>
        );

      // "text"
      default:
        return (
          <ch-edit
            id={fieldId}
            class="input"
            debounce={config.inputDebounce}
            value={value}
            onInput={this.#valueChangeHandler(field.name)}
            part={`input input--text input--${field.name}`}
          ></ch-edit>
        );
    }
  };

  #renderField = (field: DSPropertyField) => {
    const fieldId = `field-${field.name}`;
    const isReadonly =
      field.control === "readonly" || field.control === "custom";
    const isCheckbox = field.control === "checkbox";

    if (isCheckbox) {
      return (
        <div class="field field--checkbox field--full" key={field.name}>
          <div class="field__row">
            {this.#renderControl(field)}
            <label class="label" htmlFor={fieldId}>
              {field.label}
            </label>
          </div>
          {field.description && (
            <p class="field__hint body-regular-s">{field.description}</p>
          )}
        </div>
      );
    }

    return (
      <div class="field field-block" key={field.name}>
        <label
          class={{ "label": true, "label--disabled": isReadonly }}
          htmlFor={fieldId}
        >
          {field.label}
        </label>

        {this.#renderControl(field)}
      </div>
    );
  };

  #renderSection = (title: string, fields: DSPropertyField[]) => (
    <section class="field-section">
      <div class="field-section__head">
        <span class="field-section__title">{title}</span>
      </div>
      <div class="fields">{fields.map(this.#renderField)}</div>
    </section>
  );

  // #endregion Renders

  render() {
    const description = this.#componentLocale.header.description;
    const sections = this.#componentLocale.sections;

    const allFields = this.properties ?? [];
    const hasAuthSection = this.#hasTrustedConnection();

    // The Authentication section is only rendered when TRUSTED_CONNECTION is
    // present; otherwise every field stays in the always-present Connection
    // section so nothing is dropped.
    const authFields = hasAuthSection
      ? allFields.filter(this.#isAuthField)
      : [];
    const connectionFields = hasAuthSection
      ? allFields.filter(field => !this.#isAuthField(field))
      : allFields;

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <div class="hero">
            <div class="hero__badge" aria-hidden="true">
              <ch-image
                class="icon-xl"
                src={DATABASE_ICON}
                type="mask"
              ></ch-image>
            </div>
            <div class="hero__text">
              {description && (
                <p class="hero__description body-regular-s">{description}</p>
              )}
            </div>
          </div>

          <div class="main scrollable">
            {connectionFields.length > 0 &&
              this.#renderSection(sections.connection, connectionFields)}

            {hasAuthSection &&
              authFields.length > 0 &&
              this.#renderSection(sections.authentication, authFields)}
          </div>

          <footer class="dialog-footer-with-border footer">
            <div class="buttons-spacer">
              <button
                class="button-secondary"
                id="button-cancel"
                type="button"
                onClick={this.#cancelButtonClickedHandler}
                part="button-cancel"
              >
                {this.#componentLocale.footer.btnCancel}
              </button>
              <button
                class="button-primary"
                id="button-confirm"
                type="button"
                disabled={this.#hasPendingRequired()}
                onClick={this.#confirmButtonClickedHandler}
                part="button-confirm"
              >
                {this.#componentLocale.footer.btnConfirm}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type DSControlKind =
  | "checkbox"
  | "text"
  | "password"
  | "combo"
  | "combo-editable"
  | "custom"
  | "readonly";

export interface DSPropertyField {
  /** `PropertyData.name` — key used to set the property value. */
  name: string;
  /** `definition.displayName`. */
  label: string;
  /** `definition.description` (rendered below the control). */
  description?: string;
  /** `definition.category` (optional grouping). */
  category?: string;

  /** Control resolved by the host. */
  control: DSControlKind;

  /** `PropertyData.value` (masked when password). */
  value: string;
  /** `required && isDefault` (host detection). */
  missing: boolean;

  // combos:
  /** `PropertyData.standardValues`. */
  standardValues?: string[];
  /** `PropertyData.isStandardValuesExclusive`. */
  exclusive?: boolean;
}

export type CancelCallback = () => void | Promise<void>;

export type ConfirmCallback = () => void | Promise<void>;

export type PropertyChangeCallback = (
  name: string,
  value: string
) => void | Promise<void>;
